import { ITimeline } from '../Timeline';
import { EventEmitterHelper } from '../Utility/EventEmitter';
import {
    BaseWidgetConfiguration,
    FadeInCommand,
    FadeOutCommand,
    GenericWidgetConfigurationDetails,
    TimelineSlot,
    WidgetState,
    WidgetType,
    WidgetWrapperElementConfiguration,
    ZodBaseWidgetConfiguration,
} from './types';
import { Log } from '../AuxillaryService/Log';
import { isNullish } from '../Utility/Nullish';
import './Widget.scss';

/**
 * Extracts the widgetsid property from an unknown object if it exists or
 * returns "unknown" if the property does not exist.
 */
function getWidgetIdFromMaybeConfiguration(maybeWidgetConfiguration: unknown): string {
    if (maybeWidgetConfiguration instanceof Object && 'widgetsid' in maybeWidgetConfiguration) {
        const widgetsId = Reflect.get(maybeWidgetConfiguration, 'widgetsid');

        if (!isNullish(widgetsId)) {
            return String(widgetsId);
        }
    }

    return 'unknown';
}

export abstract class Widget extends EventEmitterHelper {
    /**
     * @todo: put this somewhere more appropriate
     *  Update: there is now a Nullish utility, replace uses
     * Check if given value is not empty
     */
    static valueNotEmpty<T>(val: T): val is Exclude<T, undefined | '' | null> {
        return typeof val !== 'undefined' && val != '' && null != val;
    }

    /**
     * A string to indicate what kind of widget it is (e.g. "graphic")
     */
    abstract type: WidgetType;

    /**
     * A list of the widget's metadata properties and values
     */
    config: GenericWidgetConfigurationDetails;

    id: number;

    /**
     * ID of the Zone this widget is in
     * @deprecated
     */
    zoneId: number;

    /**
     * Name of widget
     */
    name: string;

    /**
     * `name` property with escaped double quotes. Suitable to use with
     * handlebars
     */
    safeName: string;

    /**
     * Tells a time based timeline how many content slots to add for
     * a loop (in seconds)
     */
    minimumLoopTime: number;

    /**
     * Object with CSS Template versions
     * More accurately:
     * Record<TemplateId, StringifiedTemplateCSS>
     */
    cssTemplates: Record<string, string>;

    /**
     * Seconds to play widget
     */
    duration: number;

    /**
     * An integer describing the timeline ordering
     */
    order: number;

    /**
     * An integer describing the widget's plays per loop
     */
    saturation: number | null;

    /**
     * Property from API - boolean (0 for false, 1 for true) determining if
     * widget should fill entire loop
     * @deprecated
     */
    filler: boolean;

    /**
     * A boolean describing if the widget should override other widgets in zone
     */
    override: boolean;

    /**
     * An enum describing the widgets intended state
     */
    state: WidgetState = WidgetState.CREATED;

    /**
     * Used to determine if there is any content available to display
     * for a given widget
     * TODO: Refactor to be abstract, force widgets to implement getter
     */
    hasContentToPlay = true;

    /**
     * Index to use to determine where to start adding content to timeline slots
     */
    contentStartIndex = 0;

    /**
     * A boolean describing if the widget is in a broken and non-displayable
     * state
     */
    broken = false;

    /**
     * The widget's outer most DOM element
     * Event Emitter Mixin functionality is applied to this element
     */
    wrapperElement: HTMLDivElement;

    /**
     * @param maybeWidgetConfiguration - Widget configuration from API results
     * @param zoneId - ID of the Zone this widget is in
     */
    constructor(maybeWidgetConfiguration: BaseWidgetConfiguration, zoneId: number) {
        super();

        const widgetConfiguration = this.validateConfiguration(maybeWidgetConfiguration);

        this.zoneId = zoneId;
        this.id = widgetConfiguration.widgetsid;
        this.name = Widget.valueNotEmpty(widgetConfiguration.widgetsname)
            ? widgetConfiguration.widgetsname
            : widgetConfiguration.type;
        this.safeName = this.name.replace(/"/g, '\\"');

        this.minimumLoopTime = Widget.valueNotEmpty(widgetConfiguration.minimum_loop_time)
            ? Number(widgetConfiguration.minimum_loop_time)
            : 10;

        this.cssTemplates = widgetConfiguration.css_templates
            ? JSON.parse(widgetConfiguration.css_templates)
            : {};
        const customCssClass = widgetConfiguration.custom_css_class ?? null;

        this.duration = Widget.valueNotEmpty(widgetConfiguration.duration)
            ? Number(widgetConfiguration.duration)
            : 10;
        this.order = Widget.valueNotEmpty(widgetConfiguration.order)
            ? Number(widgetConfiguration.order)
            : 1;
        this.saturation = Widget.valueNotEmpty(widgetConfiguration.saturation)
            ? Number(widgetConfiguration.saturation)
            : null;
        this.filler = Number(widgetConfiguration.filler) === 1;
        this.override = Number(widgetConfiguration.override) === 1;

        this.config = { ...this._validateConfigurationDetails(widgetConfiguration.config) };

        this.wrapperElement = Widget.createWrapperElement({
            widgetId: this.id,
            widgetName: this.name,
            widgetType: widgetConfiguration.type,
            customCssClass: customCssClass,
            widgetWidth: this.config.width,
            widgetHeight: this.config.height,
            x: this.config.x,
            y: this.config.y,
            z: this.config.z,
            widgetCss: this.config.css,
            guiCss: this.config.gui_css,
        });
    }

    /**
     * Support old JSON style calls
     * @deprecated use variables/methods directly
     */
    get widget(): this {
        return this;
    }

    /**
     * @deprecated use {@link Widget.id} instead
     */
    get widgetsid(): number {
        return this.id;
    }

    /**
     * @deprecated use {@link Widget.name} instead
     */
    get widgetsname(): string {
        return this.name;
    }

    /**
     * Property - boolean (true or false) returns true if the initialize
     * function has ran
     */
    get initialized(): boolean {
        return this.state !== WidgetState.CREATED;
    }

    /**
     * Property - boolean (true or false) to determine if widget is paused
     */
    get isPaused(): boolean {
        return this.state !== WidgetState.PLAYING;
    }

    /**
     * Provides runtime validation for a widget's BaseWidgetConfiguration
     * Logs a warning providing details on discrepancies
     */
    private validateConfiguration(maybeWidgetConfiguration: unknown): BaseWidgetConfiguration {
        try {
            return ZodBaseWidgetConfiguration.parse(maybeWidgetConfiguration);
        } catch (error) {
            //Invalid!  Currently, we have no course correction, but at least we can be warned
            Log.Warning(
                `Widget (id=${getWidgetIdFromMaybeConfiguration(
                    maybeWidgetConfiguration
                )}) configuration did not pass validation`,
                null,
                __fileline
            );

            return maybeWidgetConfiguration as BaseWidgetConfiguration;
        }
    }

    /**
     * Provides runtime validation for widget specific configuration
     * Throws a ZodError providing details on discrepancies
     * This function should likely only ever be called from _validateConfigurationDetails
     */
    protected abstract validateConfigurationDetails(
        maybeWidgetConfigurationDetails: GenericWidgetConfigurationDetails
    ): GenericWidgetConfigurationDetails;

    /**
     * Handler for widget specific validation overrides to remove boilerplate code
     * @see Widget#validateConfigurationDetails
     */
    private _validateConfigurationDetails(
        maybeWidgetConfigurationDetails: GenericWidgetConfigurationDetails
    ): GenericWidgetConfigurationDetails {
        try {
            return this.validateConfigurationDetails(maybeWidgetConfigurationDetails);
        } catch (error) {
            //Invalid!  Currently, we have no course correction, but at least we can be warned
            Log.Warning(
                `Widget (id=${this.id}) configuration details did not pass validation`,
                null,
                __fileline
            );

            return maybeWidgetConfigurationDetails;
        }
    }

    /**
     * Triggered once fade in of widget is complete
     */
    async afterFadeIn(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _currentSlot: TimelineSlot,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _previousSlot: TimelineSlot | null
    ): Promise<Partial<FadeInCommand> | void> {
        return Promise.resolve(undefined);
    }

    /**
     * Triggered once fade out of widget is complete
     */
    async afterFadeOut(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _currentSlot: TimelineSlot,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _previousSlot: TimelineSlot | null
    ): Promise<void> {
        return Promise.resolve(undefined);
    }

    /**
     * Triggered before fade in occurs
     * @todo: why does the widget need access to a timeline??
     */
    async beforeFadeIn(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _currentSlot: TimelineSlot,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _previousSlot: TimelineSlot | null,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _timeline: ITimeline
    ): Promise<Partial<FadeInCommand> | void> {
        return Promise.resolve(undefined);
    }

    /**
     * triggered before fade out occurs
     * nextSlot - next slot in timeline - used to prepare next slot if same
     *      widget (if announcements are current and next slot, DOM element for
     *      next would populate and prep)
     */
    async beforeFadeOut(
        currentSlot: TimelineSlot,
        nextSlot: TimelineSlot | null
    ): Promise<Partial<FadeOutCommand> | void> {
        //cancel fade in/out if same widget
        return Promise.resolve({
            cancelFade: nextSlot !== null && nextSlot.widget.id === currentSlot.widget.id,
        });
    }

    /**
     * used to potentially clear out template cache, used only in announcements
     */
    clearCache(): void {
        return;
    }

    /**
     * used to prepare next slot - only used in announcement
     */
    contentOnDeck(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _nextSlot: TimelineSlot
    ): Promise<void> {
        // this method intentionally left blank - DO NOT add code here
        return Promise.resolve();
    }

    /**
     * Initialize Widget. This should take the widget from the created
     * state to the stopped state. Should not initialize any DOM or
     * audio at this time. Also, should not download any needed data
     * for the app to play (this can be done on the contentOnDeck function).
     * This can be used to create internal variables, timers, events, etc.
     * and to check if the content is playable on the current platform
     * (if not, set the state to degraded). Should try to set hasContentToPlay.
     * The only DOM element that can be created here is the widget
     * wrapper element.
     */
    async initialize(): Promise<void> {
        this.state = WidgetState.STOPPED;
        this.setEventEmitterElement(() => this.wrapperElement);
        this.eventEmitter.emitEvent('widget-initialize');
    }

    /**
     * Pauses widget and sets state to paused. To prevent issues with rendering
     * content, a widget will only go to the PAUSED state directly from the
     * PLAYING state. When in the PAUSED state, the widget is not playing any
     * animations, or sounds. It can still be on the dom at this point.
     */
    async pause(currentSlot: TimelineSlot): Promise<void> {
        if (this.state === WidgetState.PLAYING) {
            this.state = WidgetState.PAUSED;
            await this._doPause(currentSlot);
        }
    }

    /**
     * By default this does nothing. If needed, the individual widget should
     * override this function to pause any animations or background tasks that
     * are running.
     */
    protected async _doPause(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _currentSlot: TimelineSlot
    ): Promise<void> {
        // this method intentionally left blank - DO NOT add code here
        return Promise.resolve();
    }

    /**
     * By default this does nothing. If needed, the individual widget should
     * override this function to start getting the visuals ready to either be
     * displayed on pause or to be played. Either way this is where we
     * initialize the basic necessary DOM elements of the widget. Only gets ran
     * if the widget is coming from the STOPPED state.
     */
    async getReadyForDisplay(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _currentSlot: TimelineSlot
    ): Promise<void> {
        // this method intentionally left blank - DO NOT add code here
        return Promise.resolve();
    }

    /**
     * Starts playing content on the widget that requires sound, animations,
     * and/or visuals.
     */
    async play(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _currentSlot: TimelineSlot
    ): Promise<void> {
        this.state = WidgetState.PLAYING;
    }

    /**
     * All dom elements of the widget should be removed from the zone at this
     * point. No animations or sounds should be playing anywhere either.
     * Timers and history of progress should be stopped and reset at this time.
     */
    async stop(): Promise<void> {
        this.wrapperElement.innerText = '';
        this.state = WidgetState.STOPPED;
    }

    /**
     * Returns slots for 'Time Based' or 'Linear' timelines
     * depending on the boolean parameter
     */
    querySlotsLinear(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _timeBased?: boolean
    ): TimelineSlot[] {
        const slot = this.generateSlot(this.duration);
        return [slot];
    }

    /**
     * Returns slots for a 'Loop Based' timeline
     * numSlots - maximum number of slots
     * maxDuration - time left in loop
     */
    querySlots(numSlots: number, maxDuration: number): TimelineSlot[] {
        const slots: TimelineSlot[] = [];
        for (let i = 0; i < numSlots; ++i) {
            if (this.duration < maxDuration) {
                const slot = this.generateSlot(this.duration);
                slots.push(slot);

                maxDuration -= this.duration;
            } else {
                break;
            }
        }

        return slots;
    }

    /**
     * Build a timeline slot
     * @param duration - The amount of time this slot requires to play
     * @param groupId -  An integer if this slot belongs to a group, otherwise undefined
     */
    generateSlot(duration: number, groupId?: number): TimelineSlot<undefined, this>;
    /**
     * Build a timeline slot with parameters
     * @param duration - The amount of time this slot requires to play
     * @param groupId -  An integer if this slot belongs to a group, otherwise undefined
     * @param params -   Overridden TimelineSlot parameters, widget specific.
     *                   See {@link TimelineSlot} comments for example
     */
    generateSlot<T>(
        duration: number,
        groupId: number | undefined,
        params: T
    ): TimelineSlot<T, this>;
    generateSlot<T>(
        duration: number,
        groupId?: number,
        params?: T
    ): TimelineSlot<undefined, this> | TimelineSlot<T, this> {
        if (params) {
            return {
                widget: this,
                duration: duration,
                params: params,
                groupIdentifier: groupId ? this.generateGroupIdentifier(groupId) : null,
            };
        } else {
            return {
                widget: this,
                duration: duration,
                params: undefined,
                groupIdentifier: groupId ? this.generateGroupIdentifier(groupId) : null,
            };
        }
    }

    /**
     * Returns a standardized groupIdentifier, used to group slots
     */
    generateGroupIdentifier(groupId: number): string {
        return `widget_${this.id}_${this.safeName}_group_${groupId}`;
    }

    /**

    /**
     * Create Main DOM Element of the Widget, store it in the {@link Widget.wrapperElement} member
     */
    private static createWrapperElement(
        configuration: WidgetWrapperElementConfiguration
    ): HTMLDivElement {
        const wrapperElement = document.createElement('div');

        wrapperElement.id = `widget_${configuration.widgetId}`;

        wrapperElement.dataset.name = configuration.widgetName;

        wrapperElement.classList.add('widget');
        wrapperElement.classList.add('widget-' + configuration.widgetType);
        wrapperElement.classList.add(String(configuration.widgetType)); // DEPRECATED (use widget-{type})
        wrapperElement.classList.add(
            `custom-${configuration.widgetType}-${configuration.customCssClass}`
        );

        // css overrides
        if (Widget.valueNotEmpty(configuration.widgetWidth)) {
            wrapperElement.style.width = `${configuration.widgetWidth}px`;
        }
        if (Widget.valueNotEmpty(configuration.widgetHeight)) {
            wrapperElement.style.height = `${configuration.widgetHeight}px`;
        }
        if (Widget.valueNotEmpty(configuration.z)) {
            wrapperElement.style.zIndex = configuration.z || '1';
        }
        if (Widget.valueNotEmpty(configuration.x)) {
            wrapperElement.style.left = `${configuration.x}px`;
        }
        if (Widget.valueNotEmpty(configuration.y)) {
            wrapperElement.style.top = `${configuration.y}px`;
        }

        // user css
        if (Widget.valueNotEmpty(configuration.widgetCss)) {
            wrapperElement.setAttribute(
                'style',
                String(wrapperElement.getAttribute('style')) + ';' + configuration.widgetCss
            );
        }
        if (Widget.valueNotEmpty(configuration.guiCss)) {
            wrapperElement.setAttribute(
                'style',
                String(wrapperElement.getAttribute('style')) + ';' + configuration.guiCss
            );
        }

        return wrapperElement;
    }
}
